import Axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { getAccessTokenFromResfreshToken } from '../utils/getAccessToken'

type AuthenticatedRequestConfig = InternalAxiosRequestConfig & { _authRetry?: boolean }

let refreshRequest: Promise<string> | null = null

const refreshAccessToken = () => {
  if (!refreshRequest) {
    refreshRequest = getAccessTokenFromResfreshToken().finally(() => {
      refreshRequest = null
    })
  }
  return refreshRequest
}

const isExpiredTokenForbidden = (error: AxiosError) => {
  if (error.response?.status !== 403) return false
  const detail = (error.response.data as { detail?: unknown } | undefined)?.detail
  return typeof detail === 'string' && /token.*expired|expired.*token/i.test(detail)
}

async function authRequestInterceptor(config: InternalAxiosRequestConfig) {
  const token = await getToken()

  if (token && config && config.headers) {
    config.headers.authorization = `Bearer ${token}`
  }

  if (config.headers) {
    config.headers.Accept = 'application/json'
    config.withCredentials = false
    if (config.data instanceof FormData) {
      config.headers['Content-Type'] = 'multipart/form-data';
    } else {
      config.headers['Content-Type'] = 'application/json';
    }
  }

  return config
}

export const axios = Axios.create({
  baseURL: `${process.env.REACT_APP_VERACITY_LAB_API_BASE_URL}`,
})

axios.interceptors.request.use(authRequestInterceptor)
axios.interceptors.response.use((response) => {
  return response
}, async (error: AxiosError) => {
  const originalRequest = error.config as AuthenticatedRequestConfig | undefined
  const isTokenFailure = error.response?.status === 401 || isExpiredTokenForbidden(error)
  const isRefreshRequest = originalRequest?.url?.includes('/oe/users/initiate-auth')
  if (isTokenFailure && originalRequest && !originalRequest._authRetry && !isRefreshRequest) {
    originalRequest._authRetry = true
    try {
      const token = await refreshAccessToken()
      originalRequest.headers.Authorization = `Bearer ${token}`
      return axios(originalRequest)
    } catch (refreshError) {
      return Promise.reject(refreshError)
    }
  }
  return Promise.reject(error)
})

export const getToken = async () => {
  const token = localStorage.getItem('access_token')
  return token
}
